<template>
  <v-container>
    <v-card>
      <v-card-title>
        Plantillas de correo
        <v-spacer />
        <!-- La papelera: lo borrado se puede restaurar. El borrado es logico,
             asi que una plantilla borrada por error vuelve entera, con los
             Envios que la usaban. -->
        <v-btn text :disabled="saving" class="mr-2" @click="toggleTrash">
          <v-icon left>{{ showTrash ? "expand_less" : "delete_outline" }}</v-icon>
          Papelera
        </v-btn>
        <v-btn class="primary" :disabled="saving" @click="newTemplate">Nueva plantilla</v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>

        <v-data-table
          :headers="headers"
          :items="templates"
          :loading="loading"
          no-data-text="No hay plantillas cargadas"
          loading-text=""
          :footer-props="{ itemsPerPageOptions: [15, 50, 100], itemsPerPageText: 'Mostrar' }"
        >
          <template v-slot:item.name="{ item }">
            <!-- plainText: un nombre guardado con marcado se mostraria crudo
                 ("<p>Prueba</p>") en la columna. Mismo criterio que la pantalla
                 de Envios. -->
            {{ plainText(item.name) }}
          </template>
          <template v-slot:item.purpose="{ item }">
            {{ purposeText(item.purpose) }}
          </template>
          <template v-slot:item.visualStyle="{ item }">
            {{ visualStyleText(item.visualStyle) }}
          </template>
          <template v-slot:item.isActive="{ item }">
            {{ item.isActive ? "Sí" : "No" }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon :disabled="saving" @click="editTemplate(item)">
              <v-icon color="primary">mode_edit</v-icon>
            </v-btn>
            <v-btn icon :disabled="saving" @click="remove(item)">
              <v-icon color="error">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-card v-if="showTrash" class="mt-4 trash-card" outlined>
      <v-card-title class="subtitle-1">
        <v-icon left color="grey darken-1">delete_outline</v-icon>
        Plantillas borradas
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="trashHeaders"
          :items="deletedTemplates"
          :loading="loadingTrash"
          no-data-text="La papelera está vacía"
          loading-text=""
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.name="{ item }">
            {{ plainText(item.name) }}
          </template>
          <template v-slot:item.purpose="{ item }">
            {{ purposeText(item.purpose) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small text color="primary" :disabled="saving" @click="restore(item)">
              <v-icon small left>restore</v-icon>Restaurar
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog :value="showDialog" max-width="900" persistent @input="cancelEdit">
      <!-- v-if, no solo el :value del dialogo: v-dialog usa el mixin bootable,
           que una vez abierto deja el contenido montado para siempre
           (hasContent = isBooted || eager || isActive). Sin esto el formulario
           sobrevive al cierre con templateForEdit en null -mismo bug que ya
           paso en la pantalla de empresas-. -->
      <edit-email-template
        v-if="templateForEdit"
        :template="templateForEdit"
        :purpose-options="purposes"
        :visual-style-options="visualStyles"
        @cancel="cancelEdit"
        @saved="onSaved"
      />
    </v-dialog>
  </v-container>
</template>

<script>
"use strict";

import emailTemplateService from "@/services/EmailTemplateService";
import editEmailTemplate from "@/components/views/emails/edit-email-template.view.vue";
import plainTextOf from "@/filters/plain-text";

export default {
  name: "list-email-templates",
  components: { editEmailTemplate },
  data() {
    return {
      templates: [],
      purposes: [],
      visualStyles: [],
      loading: false,
      // Bandera "en vuelo": sin esto, un borrado que falla deja la fila en la
      // tabla como si hubiera funcionado y no bloquea el resto de los botones.
      saving: false,
      errorMessage: null,
      showDialog: false,
      templateForEdit: null,
      showTrash: false,
      loadingTrash: false,
      deletedTemplates: [],
      trashHeaders: [
        { text: "Nombre", value: "name" },
        { text: "Propósito", value: "purpose" },
        { text: "Acciones", value: "actions", sortable: false, width: 140 }
      ],
      headers: [
        { text: "Nombre", value: "name" },
        { text: "Propósito", value: "purpose" },
        { text: "Estilo visual", value: "visualStyle" },
        { text: "Activo", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false, width: 120 }
      ]
    };
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.errorMessage = null;
      this.loading = true;
      try {
        const [templates, purposes, visualStyles] = await Promise.all([
          emailTemplateService.list(),
          emailTemplateService.purposes(),
          emailTemplateService.visualStyles()
        ]);
        this.templates = templates;
        this.purposes = purposes;
        this.visualStyles = visualStyles;
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudieron cargar las plantillas");
      } finally {
        this.loading = false;
      }
    },
    // El backend manda el motivo real (nombre repetido, por ejemplo). Se
    // muestra tal cual y solo se cae al texto propio si no vino ninguno.
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    plainText(value) {
      return plainTextOf(value);
    },
    // El texto visible sale del lookup, no de una lista escrita a mano en la
    // pantalla: dos listas que hay que mantener en acuerdo terminan en
    // desacuerdo.
    purposeText(value) {
      const purpose = this.purposes.find(p => p.value === value);
      return (purpose && purpose.text) || value;
    },
    visualStyleText(value) {
      const style = this.visualStyles.find(s => s.value === value);
      return (style && style.text) || value;
    },
    newTemplate() {
      this.templateForEdit = {
        name: "",
        purpose: "",
        visualStyle: 1,
        header: "",
        bodyText: "",
        signature: "",
        theme: {
          fontFamily: "",
          fontSize: "",
          dueDateColor: "",
          tableBorders: null,
          tableStriped: null
        },
        isActive: true
      };
      this.showDialog = true;
    },
    editTemplate(item) {
      this.templateForEdit = JSON.parse(JSON.stringify(item));
      this.showDialog = true;
    },
    cancelEdit() {
      this.showDialog = false;
      this.templateForEdit = null;
    },
    async onSaved() {
      this.cancelEdit();
      await this.load();
    },
    async remove(item) {
      // Confirmacion antes de borrar, como en la pantalla de Envios: el
      // dialogo global (mixin botConfirmDialog) resuelve con OK y rechaza con
      // Cancelar. Una plantilla borrada por error deja sin salir todos los
      // Envios que la usan.
      try {
        await this.botConfirmDialog({
          title: "Borrar plantilla",
          content: `¿Borrar la plantilla "${this.plainText(item.name)}" (${this.purposeText(item.purpose)})? ` +
            "Los Envíos que la usan dejan de salir. Se puede recuperar desde la papelera."
        });
      } catch (cancelled) {
        return;
      }
      this.errorMessage = null;
      this.saving = true;
      try {
        await emailTemplateService.remove(item._id);
        await this.load();
        if (this.showTrash) await this.loadTrash();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo borrar la plantilla");
      } finally {
        this.saving = false;
      }
    },
    async toggleTrash() {
      this.showTrash = !this.showTrash;
      if (this.showTrash) await this.loadTrash();
    },
    async loadTrash() {
      this.loadingTrash = true;
      try {
        this.deletedTemplates = await emailTemplateService.listDeleted();
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo cargar la papelera");
      } finally {
        this.loadingTrash = false;
      }
    },
    async restore(item) {
      this.errorMessage = null;
      this.saving = true;
      try {
        await emailTemplateService.restore(item._id);
        await Promise.all([this.load(), this.loadTrash()]);
      } catch (error) {
        this.errorMessage = this.messageOf(error, "No se pudo restaurar la plantilla");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
